import * as THREE from 'three'
import RotationPad from './RotationPad.js'
import MovementPad from './MovementPad.js'
import ZoomButtons from "./ZoomButtons.js";
import SprintButton from "./SprintButton";
import PerspectiveButton from "./PerspectiveButton";


//TODO Remove raycast

class TouchControls {
	containerElement
	rotationPad
	movementPad
	container
	camera
	config
	mouse
	enabled = true
	#rotationMatrices
	#hitObjects
	#velocity
	#maxPitch
	#isRightMouseDown = false
	#isSprintButtonDown = false
	#moveForward = false
	#moveBackward = false
	#moveLeft = false
	#moveRight = false
	#moveForwardLocked = false
	#moveBackwardLocked = false
	#moveLeftLocked = false
	#moveRightLocked = false
	#ztouch = 1
	#xtouch = 1

	constructor(camera, canvasElement, options = {}) {
		this.containerElement = document.createElement('div')
		this.containerElement.classList.add('touch-container')
		this.camera = camera

		this.container = canvasElement.parentElement
		this.container.appendChild(this.containerElement)
		this.config = Object.assign({
			showRotateButton: false,
			showPerspectiveButton: true,
			showZoomButtons: true,
			showSprintButton: true,
			rotationSpeed: 0.02,   // coefficient of rotation
			maxPitch: 55,           // max camera pitch angle
			hitTest: true,          // stop on hitting objects
			hitTestDistance: 40     // distance to test for hit
		}, options)

		this.#rotationMatrices = []
		this.#hitObjects = []
		this.#maxPitch = this.config.maxPitch * Math.PI / 180
		this.#velocity = new THREE.Vector3(0, 0, 0)
		this.mouse = new THREE.Vector2()

		if (this.config.showZoomButtons) {
			const zoomButton = new ZoomButtons(this.containerElement, canvasElement)
			zoomButton.padElement.addEventListener('wheel', (event) => {
				window.dispatchEvent(new CustomEvent("wheel", {
						detail: Object.assign({ui_click: true}, event.detail)
					}
				))
			});
		}

		if (this.config.showPerspectiveButton) {
			const btn = new PerspectiveButton(this.containerElement, canvasElement)
			btn.padElement.addEventListener('toggle', (event) => {
				console.log("PerspectiveButton", event)
				window.dispatchEvent(new KeyboardEvent("keyup", {
						key: 'p'
					}
				))
			});
		}

		if (this.config.showSprintButton) {
			const sprintButton = new SprintButton(this.containerElement, canvasElement)
			sprintButton.padElement.addEventListener('toggle', (event) => {
				this.#isSprintButtonDown = !this.#isSprintButtonDown
				this.onChange()
			});
		}

		// Creating rotation pad
		if (this.config.showRotateButton) {
			this.rotationPad = new RotationPad(this.containerElement, canvasElement)
			this.rotationPad.padElement.addEventListener('YawPitch', (event) => {
				let rotation = this.#calculateCameraRotation(event.detail.deltaX, event.detail.deltaY)
				if (rotation.rx)
					this.camera.rotation.x = rotation.rx
				if (rotation.ry)
					this.camera.rotation.y = rotation.ry
			})
		}

		// Creating movement pad
		this.movementPad = new MovementPad(this.containerElement, canvasElement)
		this.movementPad.padElement.addEventListener('move', (event) => {
			this.#ztouch = Math.abs(event.detail.deltaY)
			this.#xtouch = Math.abs(event.detail.deltaX)

			if (event.detail.deltaY === event.detail.middle) {
				this.#ztouch = 1
				this.#moveForward = this.#moveBackward = false
			} else {
				if (event.detail.deltaY > event.detail.middle) {
					this.#moveForward = true
					this.#moveBackward = false
				} else if (event.detail.deltaY < event.detail.middle) {
					this.#moveForward = false
					this.#moveBackward = true
				}
			}

			if (event.detail.deltaX === event.detail.middle) {
				this.#xtouch = 1
				this.#moveRight = this.#moveLeft = false
			} else {
				if (event.detail.deltaX < event.detail.middle) {
					this.#moveRight = true
					this.#moveLeft = false
				} else if (event.detail.deltaX > event.detail.middle) {
					this.#moveRight = false
					this.#moveLeft = true
				}
			}
			this.onChange()
		})
		this.movementPad.padElement.addEventListener('stopMove', () => {
			console.log("stopMove")
			this.#ztouch = this.#xtouch = 1
			this.#moveForward = this.#moveBackward = this.#moveLeft = this.#moveRight = false
			this.onChange()
		})

		this.container.addEventListener('contextmenu', (event) => {
			event.preventDefault()
		})
		this.container.addEventListener('mousedown', (event) => this.onMouseDown(event))
		this.container.addEventListener('mouseup', (event) => this.onMouseUp(event))

		document.addEventListener('mousemove', (event) => this.onMouseMove(event))
		document.addEventListener('mouseout', (event) => this.onMouseOut(event))


		document.addEventListener('touchmove', (e) => e.preventDefault(), {passive: false})

		this.#prepareRotationMatrices()
	}

	onMouseDown(event) {
		if (this.enabled && event.button === 2) {
			this.#isRightMouseDown = true
			event.preventDefault()
			event.stopPropagation()
		}
	}

	onMouseUp(event) {
		if (this.enabled && event.button === 2) {
			this.#isRightMouseDown = false
		}
	}

	onMouseMove(event) {
		this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1
		this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1

		if (!this.enabled || !this.#isRightMouseDown)
			return

		let movementX = event.movementX || 0
		let movementY = event.movementY || 0
		let rotation = this.#calculateCameraRotation(-1 * movementX, -1 * movementY)

		// this.setRotation(rotation.rx, rotation.ry)
	}

	onMouseOut() {
		this.#isRightMouseDown = false
	}

	#prepareRotationMatrices() {
		let rotationMatrixF = new THREE.Matrix4()
		rotationMatrixF.makeRotationY(0)
		this.#rotationMatrices.push(rotationMatrixF)  // forward direction

		let rotationMatrixB = new THREE.Matrix4()
		rotationMatrixB.makeRotationY(180 * Math.PI / 180)
		this.#rotationMatrices.push(rotationMatrixB)  // backward direction

		let rotationMatrixL = new THREE.Matrix4()
		rotationMatrixL.makeRotationY(90 * Math.PI / 180)
		this.#rotationMatrices.push(rotationMatrixL)  // left direction

		let rotationMatrixR = new THREE.Matrix4()
		rotationMatrixR.makeRotationY((360 - 90) * Math.PI / 180)
		this.#rotationMatrices.push(rotationMatrixR)  // right direction
	}

	#calculateCameraRotation(dx, dy, factor) {
		let rFactor = factor ? factor : this.config.rotationSpeed
		let ry = this.camera.rotation.y - (dx * rFactor) //TODO Broken
		let rx = this.camera.rotation.x - (dy * rFactor)
		rx = Math.max(-this.#maxPitch, Math.min(this.#maxPitch, rx))

		return {
			rx: rx,
			ry: ry
		}
	}

	#lockDirectionByIndex(index) {
		if (index === 0)
			this.lockMoveForward(true)
		else if (index === 1)
			this.lockMoveBackward(true)
		else if (index === 2)
			this.lockMoveLeft(true)
		else if (index === 3)
			this.lockMoveRight(true)
	}

	update() {
		if (this.config.hitTest)
			this.hitTest()

		// this.#velocity.x += (-1 * this.#velocity.x) * this.config.delta
		// this.#velocity.z += (-1 * this.#velocity.z) * this.config.delta

		if (this.#moveForward && !this.#moveForwardLocked) {
			// this.#velocity.z -= this.#ztouch * this.config.moveSpeed * this.config.delta
		}
		if (this.#moveBackward && !this.#moveBackwardLocked) {
			// this.#velocity.z += this.#ztouch * this.config.moveSpeed * this.config.delta
		}
		if (this.#moveLeft && !this.#moveLeftLocked) {
			// this.#velocity.x -= this.#xtouch * this.config.moveSpeed * this.config.delta
		}
		if (this.#moveRight && !this.#moveRightLocked) {
			// this.#velocity.x += this.#xtouch * this.config.moveSpeed * this.config.delta
		}


		//TODO SEND EVENT AROUND CURRENT MOVEMENT
	}

	hitTest() {
		return

		// this.unlockAllDirections()
		// this.#hitObjects = []
		// let cameraDirection = this.getDirection2(new THREE.Vector3(0, 0, 0)).clone()
		//
		// for (let i = 0; i < 4; i++) {
		// 	// Apply rotation for each direction
		// 	let direction = cameraDirection.clone()
		// 	direction.applyMatrix4(this.#rotationMatrices[i])
		//
		// 	let rayCaster = new THREE.Raycaster(this.fpsBody.position, direction)
		// 	let intersects = rayCaster.intersectObject(this.#scene, true)
		// 	if ((intersects.length > 0 && intersects[0].distance < this.config.hitTestDistance)) {
		// 		this.#lockDirectionByIndex(i)
		// 		this.#hitObjects.push(intersects[0])
		// 	}
		// }
		//
		// return this.#hitObjects
	}

	onChange() {
		let evt = new CustomEvent('change', {
			bubbles: false,
			detail: {
				fwd: this.#moveLeft,
				back: this.#moveBackward,
				left: this.#moveLeft,
				right: this.#moveRight,
				space: false,
				shift: this.#isSprintButtonDown
			}
		})
		this.containerElement.dispatchEvent(evt)
	}


	lockMoveForward(isLocked) {
		this.#moveForwardLocked = isLocked
	}

	lockMoveBackward(isLocked) {
		this.#moveBackwardLocked = isLocked
	}

	lockMoveLeft(isLocked) {
		this.#moveLeftLocked = isLocked
	}

	lockMoveRight(isLocked) {
		this.#moveRightLocked = isLocked
	}

	unlockAllDirections() {
		this.lockMoveForward(false)
		this.lockMoveBackward(false)
		this.lockMoveLeft(false)
		this.lockMoveRight(false)
	}

	stopMouseMoving() {
		this.#isRightMouseDown = false
	}

	getHitObjects() {
		return this.#hitObjects
	}
}


export default TouchControls
